import React, { useState, useEffect, useRef } from 'react';
import { motion, useAnimation, AnimatePresence, useInView } from 'framer-motion';
import { 
  ChevronDownIcon, 
  CursorClickIcon, 
  LightningBoltIcon, 
  ChartBarIcon, 
  FilterIcon,
  CashIcon, 
  LocationMarkerIcon,
  MenuIcon,
  XIcon,
  CheckCircleIcon,
  UserGroupIcon,
  CubeTransparentIcon,
  ShieldCheckIcon,
  BadgeCheckIcon,
  SparklesIcon,
  CurrencyDollarIcon,
  EyeIcon,
  PhotographIcon,
  ClockIcon,
  ArrowRightIcon,
  PlusIcon,
  ChevronRightIcon,
  SearchIcon,
  CalendarIcon,
  ChartPieIcon,
  CreditCardIcon,
  UserIcon,
  DeviceMobileIcon,
  GlobeAltIcon
} from '@heroicons/react/outline';

// Composant principal de la Landing Page
const FooterButton = () => {
  const [scrollY, setScrollY] = useState(0);
    const [navbarOpen, setNavbarOpen] = useState(false);
    const [isNavTransparent, setIsNavTransparent] = useState(true);
    const [activeSlide, setActiveSlide] = useState(0);
    
    // Références pour l'animation au scroll
    const heroRef = useRef(null);
    const statsRef = useRef(null);
    const featuresRef = useRef(null);
    const platformRef = useRef(null);
    const screensRef = useRef(null);
    const partnersRef = useRef(null);
    const testimonialsRef = useRef(null);
    
    // InView Hooks pour déclencher les animations
    const isHeroInView = useInView(heroRef, { once: false });
    const isStatsInView = useInView(statsRef, { once: false });
    const isFeaturesInView = useInView(featuresRef, { once: false });
    const isPlatformInView = useInView(platformRef, { once: false });
    const isScreensInView = useInView(screensRef, { once: false });
    const isPartnersInView = useInView(partnersRef, { once: false });
    const isTestimonialsInView = useInView(testimonialsRef, { once: false });
    
    // Animations contrôlées
    const heroControls = useAnimation();
    const statsControls = useAnimation();
    const featuresControls = useAnimation();
    const platformControls = useAnimation();
    const screensControls = useAnimation();
    const partnersControls = useAnimation();
    const testimonialsControls = useAnimation();
    
    useEffect(() => {
      const handleScroll = () => {
        setScrollY(window.scrollY);
        setIsNavTransparent(window.scrollY < 50);
      };
      
      window.addEventListener('scroll', handleScroll);
      
      // Timer pour slideshow de la Hero Section
      const slideshowTimer = setInterval(() => {
        setActiveSlide((prev) => (prev + 1) % 3);
      }, 5000);
      
      return () => {
        window.removeEventListener('scroll', handleScroll);
        clearInterval(slideshowTimer);
      };
    }, []);
    
    // Effet pour les animations au scroll
    useEffect(() => {
      if (isHeroInView) heroControls.start("visible");
      if (isStatsInView) statsControls.start("visible");
      if (isFeaturesInView) featuresControls.start("visible");
      if (isPlatformInView) platformControls.start("visible");
      if (isScreensInView) screensControls.start("visible");
      if (isPartnersInView) partnersControls.start("visible");
      if (isTestimonialsInView) testimonialsControls.start("visible");
    }, [
      isHeroInView, isStatsInView, isFeaturesInView, isPlatformInView, 
      isScreensInView, isPartnersInView, isTestimonialsInView,
      heroControls, statsControls, featuresControls, platformControls, 
      screensControls, partnersControls, testimonialsControls
    ]);

  
    // Effets d'animation Framer Motion
    const fadeInUp = {
      hidden: { opacity: 0, y: 30 },
      visible: { 
        opacity: 1, 
        y: 0,
        transition: { 
          duration: 0.8,
          ease: "easeOut"
        }
      }
    };
    
    const staggerContainer = {
      hidden: { opacity: 0 },
      visible: {
        opacity: 1,
        transition: {
          staggerChildren: 0.1
        }
      }
    };
    
    const scaleIn = {
      hidden: { opacity: 0, scale: 0.8 },
      visible: { 
        opacity: 1, 
        scale: 1,
        transition: { duration: 0.6 }
      }
    };
    
    const slideInLeft = {
      hidden: { opacity: 0, x: -50 },
      visible: { 
        opacity: 1, 
        x: 0,
        transition: { duration: 0.7 }
      }
    };
    
    const slideInRight = {
      hidden: { opacity: 0, x: 50 },
      visible: { 
        opacity: 1, 
        x: 0,
        transition: { duration: 0.7 }
      }
    };

   return (
      <div>

            <div className="fixed bottom-6 right-6 z-50 flex flex-col space-y-3">
                    {/* Bouton de chat */}
                    <motion.button 
                      className="bg-[#E84E0E] text-white p-4 rounded-full shadow-lg flex items-center justify-center"
                      whileHover={{ scale: 1.1, boxShadow: "0 10px 25px -5px rgba(232, 78, 14, 0.4)" }}
                      whileTap={{ scale: 0.95 }}
                      initial={{ scale: 0, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{ delay: 1, type: "spring", stiffness: 200, damping: 15 }}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                      </svg>
                    </motion.button>
                    
                    {/* Bouton retour en haut */}
                    <motion.a 
                      href="#accueil"
                      className="bg-white text-gray-800 p-3 rounded-full shadow-lg flex items-center justify-center border border-gray-200"
                      whileHover={{ scale: 1.1, boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)" }}
                      whileTap={{ scale: 0.95 }}
                      initial={{ scale: 0, opacity: 0 }}
                      animate={{ 
                        scale: scrollY > 300 ? 1 : 0,
                        opacity: scrollY > 300 ? 1 : 0,
                      }}
                      transition={{ type: "spring", stiffness: 200, damping: 15 }}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                      </svg>
                    </motion.a>
            </div>
        {/* Autres sections seront ajoutées ici */}
      </div>
    );
  };
  export default FooterButton;